// Sistem Yönetimi — süper adminin kendi hesabı.
//
// Neden ayrı ekran: tenant tarafındaki Profil ekranı (ekran 13) tesis kapsamı
// varsayıyor — bildirim matrisi, tesis listesi, rol rozetleri. Süper adminin
// tesisi yok ve o ekran onun için yarısı boş çalışırdı.
//
// UÇ AYNI: `/api/profil/sifre`. O uçta izin kapısı yok (yalnız kimlik doğrulama)
// ve şifre kuralları tek yerde duruyor — mevcut şifre doğrulaması, güç ölçüsü,
// "yeni şifre eskisiyle aynı olamaz" ve diğer oturumların kapatılması dâhil.
// İkinci bir uç açmak o kuralların ikiye ayrılıp zamanla ayrışması demekti.
//
// Kurulumda şifre `/opt/btys/.env` içinden geliyor (BTYS_SISTEM_SIFRE) ve o
// dosyada düz metin duruyor. Bu ekran ilk girişten sonra şifreyi oradan
// bağımsız hâle getirmenin yolu: değiştirildiği anda .env'deki değer artık
// geçerli değil.

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { sifreOlc, SIFRE_ASGARI_UZUNLUK } from '../../lib/sifreGucu'
import { tr, trTarihSaat } from '../../lib/trFormat'
import { useOturum } from '../../lib/oturum'
import { profil as profilApi } from '../account/api'
import SistemKabuk from './SistemKabuk'

function hataMetni(h: unknown) {
  if (!(h instanceof ApiHatasi)) return 'Beklenmeyen bir hata oluştu.'
  // Sunucu güç kuralında eksikleri detay listesinde veriyor; kullanıcıya
  // "yeterince güçlü değil" demek yetmiyor, hangi kuralın eksik olduğu lazım.
  return h.detay?.length ? `${h.message} — ${h.detay.join(' · ')}` : h.message
}

function Icerik() {
  const { durum } = useOturum()
  const kuyruk = useQueryClient()

  const [mevcut, setMevcut] = useState('')
  const [yeni, setYeni] = useState('')
  const [tekrar, setTekrar] = useState('')

  const olcum = useMemo(() => sifreOlc(yeni), [yeni])
  const eslesiyor = tekrar.length === 0 || yeni === tekrar

  const { data: p } = useQuery({ queryKey: ['profil'], queryFn: profilApi.getir })

  const guncelle = useMutation({
    mutationFn: () => profilApi.sifreDegistir({ mevcut, yeni }),
    onSuccess: async (sonuc) => {
      setMevcut('')
      setYeni('')
      setTekrar('')
      await kuyruk.invalidateQueries({ queryKey: ['profil'] })
      toast(
        sonuc.kapatilanOturum > 0
          ? `Şifre güncellendi — diğer ${tr(sonuc.kapatilanOturum, 0)} oturum sonlandırıldı`
          : 'Şifre güncellendi',
      )
    },
    onError: (h) => toast(hataMetni(h)),
  })

  function gonder() {
    if (!mevcut) return toast('Mevcut şifrenizi girin')
    if (yeni !== tekrar) return toast('Yeni şifre tekrarı eşleşmiyor')
    if (!olcum.yeterli)
      return toast(`Şifre yeterince güçlü değil — ${olcum.eksikler.join(' · ')}`)
    guncelle.mutate()
  }

  return (
    <>
      <div className="crumb">
        Sistem Yönetimi <b>/ Hesabım</b>
      </div>

      <div className="page-h">
        <h1 className="page-t">Hesabım</h1>
      </div>

      <div className="card">
        <div className="sect-t">Hesap</div>
        <table className="grid">
          <thead>
            <tr>
              <th style={{ width: 200 }}>Alan</th>
              <th style={{ width: 280 }}>Değer</th>
              <th>Açıklama</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Ad</td>
              <td className="mono" style={{ fontWeight: 600 }}>
                {durum?.kullanici.ad ?? '—'}
              </td>
              <td className="cell-sub">Üst çubukta görünen ad</td>
            </tr>
            <tr>
              <td>E-posta</td>
              <td className="mono" style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                {durum?.kullanici.eposta ?? '—'}
              </td>
              <td className="cell-sub">Giriş kimliği · sunucudaki .env ile eşleşir</td>
            </tr>
            <tr>
              <td>Rol</td>
              <td>
                <span className="badge b-navy">Süper Admin</span>
              </td>
              <td className="cell-sub">
                Tüm şirketleri yönetir; tesis modüllerinin tamamına kapalıdır
              </td>
            </tr>
            <tr>
              <td>Son şifre değişikliği</td>
              <td className="mono" style={{ fontWeight: 600 }}>
                {p?.sifreDegisimZamani ? trTarihSaat(p.sifreDegisimZamani) : '—'}
              </td>
              <td className="cell-sub">
                {p?.sifreDegisimZamani
                  ? `${tr(p.sifrePolitikaGun ?? 180, 0)} günde bir yenilenmesi önerilir`
                  : 'Şifre hiç değiştirilmedi'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card" style={{ marginTop: 8 }}>
        <div className="sect-t">Şifre Değiştir</div>

        <div className="info-box" style={{ margin: 12 }}>
          <b>Kurulum şifresi sunucuda düz metin duruyor.</b>
          <span>
            Hesap ilk açılışta <span className="mono">/opt/btys/.env</span> içindeki
            <span className="mono"> BTYS_SISTEM_SIFRE</span> değeriyle kuruldu. Buradan
            değiştirdiğiniz anda o değer geçersiz kalır — dosyadaki satır artık yalnız
            kurulum kaydıdır, giriş şifresi değil.
          </span>
        </div>

        <div style={{ padding: '0 12px 12px' }}>
          <div className="fgrid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
            <div className="fld">
              <label htmlFor="sa-mevcut">Mevcut şifre</label>
              <input
                id="sa-mevcut"
                className="in"
                type="password"
                placeholder="••••••••"
                value={mevcut}
                onChange={(e) => setMevcut(e.target.value)}
              />
            </div>

            <div className="fld">
              <label htmlFor="sa-yeni">Yeni şifre</label>
              <input
                id="sa-yeni"
                className="in"
                type="password"
                placeholder={`En az ${SIFRE_ASGARI_UZUNLUK} karakter`}
                value={yeni}
                onChange={(e) => setYeni(e.target.value)}
              />
              <div className="pwbar">
                <i style={{ width: olcum.genislik, background: olcum.renk }} />
              </div>
              <div className="fld-hint">
                Güç: {olcum.etiket} · harf + rakam + özel karakter önerilir
              </div>
            </div>

            {/* Uyuşmazlık kaydı ENGELLİYOR — uyarı değil doğrulama hatası,
                bu yüzden `.fld.err` kalıbı (Profil ekranıyla aynı). */}
            <div className={eslesiyor ? 'fld' : 'fld err'}>
              <label htmlFor="sa-tekrar">Yeni şifre (tekrar)</label>
              <input
                id="sa-tekrar"
                className="in"
                type="password"
                value={tekrar}
                onChange={(e) => setTekrar(e.target.value)}
              />
              {!eslesiyor && <div className="fld-hint">Tekrar eşleşmiyor.</div>}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 8,
              gap: 12,
            }}
          >
            <span className="cell-sub">
              Şifre değişince <b>bu oturum dışındaki</b> tüm oturumlar sonlandırılır.
            </span>
            <button
              className="btn btn-pri"
              type="button"
              disabled={guncelle.isPending || !mevcut || !yeni || !eslesiyor}
              onClick={gonder}
            >
              Şifreyi güncelle
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default function HesapSayfasi() {
  return (
    <SistemKabuk>
      <Icerik />
    </SistemKabuk>
  )
}
